import { CSSObject } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

// Built once at module load instead of on every call. Previously every row
// (via TableCheckbox and useCompaniesCheckboxFunctions) allocated this whole
// object on each render, which also forced Emotion to re-serialize it.
// The only value that depends on an argument is tableInlineActionsBlock
// (rowSwitch truthy/falsy), so we build exactly two variants.
const buildTableStyles = (hasRowSwitch: boolean): CSSObject =>
  ({
    tableToolbar: {
      display: "flex",
      alignItems: "center"
    },
    tableViewGridCSS: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",

      "@media(max-width: 839px)": {
        justifyContent: "flex-start",
        alignItems: "flex-start"
      },

      "@media(max-width: 599px)": {
        flexDirection: "column",
        alignItems: "flex-start"
      }
    },
    tableFooter: {
      display: "flex",
      alignItems: "center",
      position: "absolute",
      bottom: "0",
      width: "100%"
    },
    tableCheckboxCell: {
      position: "sticky",
      zIndex: 3,
      left: "0"
    },
    tableCheckboxDiv: {
      height: "24px",
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center"
    },
    desktopTableHeadActive: {
      position: "sticky",
      top: "0",
      left: "0",
      width: "100%",
      boxSizing: "border-box",
      marginTop: "-42px",
      zIndex: 5,
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      rowGap: "8px",
      borderRadius: "unset",
      padding: "4px 24px 4px 16px"
    },
    mobileTableHead: {
      position: "sticky",
      top: "0",
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      rowGap: "8px",
      zIndex: 10,
      padding: "4px 24px",
      background: colors.blue100,
      borderTop: `1px solid ${colors.blue200}`,
      borderBottom: `1px solid ${colors.blue200}`
    },
    mobileTableItemBox: {
      background: colors.gray100,
      transition: "opacity 300ms ease-out",

      "& > div + div": {
        marginTop: "8px"
      }
    },
    activeMobileTableItemCheckbox: {
      padding: "16px 8px 16px 16px"
    },
    mobileTableItem: {
      flex: "1",
      overflow: "hidden",

      "& > div": {
        borderBottom: `1px solid ${colors.gray200}`,

        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "56px",
        boxSizing: "border-box",
        padding: "0 24px",
        "&:last-of-type": {
          borderBottom: "none"
        }
      }
    },
    mobileTableColumn: {
      "& > div": {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
      }
    },
    mobileTableColumnValue: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "20px",
      letterSpacing: "0.1px"
    },
    emptyTable: {
      top: "0",
      bottom: "0",
      left: "0",
      right: "0",
      overflow: "unset"
    },
    emptyTableInnerDiv: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      flex: "1",
      padding: "32px 24px",
      overflowY: "auto",

      "& > div:nth-of-type(1)": {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: "16px"
      }
    },
    emptyTableTitle: {
      color: colors.textDark,
      marginBottom: "8px"
    },
    emptyTableDescription: {
      color: colors.textSecondary,
      textAlign: "center",
      marginBottom: "12px",
      fontWeight: 400,
      whiteSpace: "pre-line"
    },
    tableCircularProgressDiv: {
      display: "flex",
      justifyContent: "center",
      position: "absolute",
      top: "150px",
      width: "100%",
      zIndex: 11
    },
    noResultsIcon: {
      fontSize: "120px"
    },
    tableInlineActionsBlock: {
      display: "flex",
      justifyContent: "end",

      "& button + button": {
        marginLeft: "12px"
      },
      "& button:first-of-type": {
        marginLeft: hasRowSwitch ? "12px" : "18px"
      },
      "& button:last-of-type": {
        marginRight: "18px"
      }
    },
    firstTableInlineAction: {
      background: colors.gray200,
      position: "absolute",
      top: "0",
      bottom: "0",
      left: "0",
      width: "1px"
    },
    mobileTableInlineActionsBlock: {
      flexDirection: "column",
      justifyContent: "center"
    },
    companyTitleDivInDesktopCell: {
      maxWidth: "calc(100% - 40px)"
    },
    driverLastActivityDiv: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start"
    },
    driverLastActivityIconDiv: {
      position: "relative",
      display: "flex",
      alignItems: "center"
    },

    headCheckboxInnerStyle: {
      height: "24px",
      display: "flex",
      alignItems: "center"
    },

    checkboxCellStyle: { zIndex: 4, position: "sticky", left: "0" }
  }) as CSSObject;

const tableStylesWithRowSwitch = buildTableStyles(true);
const tableStylesWithoutRowSwitch = buildTableStyles(false);

export const useTableStyles = (rowSwitch?: any): CSSObject =>
  rowSwitch ? tableStylesWithRowSwitch : tableStylesWithoutRowSwitch;

export const staticTableStyles: CSSObject = tableStylesWithoutRowSwitch;
